import React, { useState, useEffect } from 'react';
import { 
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Button,
  Paper,
  Container
} from '@mui/material';
import { useCheckout } from '../../contexts/CheckoutContext';

// US states list for dropdown
const usStates = [
  { value: 'alabama', label: 'Alabama' },
  { value: 'alaska', label: 'Alaska' },
  { value: 'arizona', label: 'Arizona' },
  { value: 'arkansas', label: 'Arkansas' },
  { value: 'california', label: 'California' },
  { value: 'colorado', label: 'Colorado' },
  { value: 'connecticut', label: 'Connecticut' },
  { value: 'delaware', label: 'Delaware' },
  { value: 'florida', label: 'Florida' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'hawaii', label: 'Hawaii' },
  { value: 'idaho', label: 'Idaho' },
  { value: 'illinois', label: 'Illinois' },
  { value: 'indiana', label: 'Indiana' },
  { value: 'iowa', label: 'Iowa' },
  { value: 'kansas', label: 'Kansas' },
  { value: 'kentucky', label: 'Kentucky' },
  { value: 'louisiana', label: 'Louisiana' },
  { value: 'maine', label: 'Maine' },
  { value: 'maryland', label: 'Maryland' },
  { value: 'massachusetts', label: 'Massachusetts' },
  { value: 'michigan', label: 'Michigan' },
  { value: 'minnesota', label: 'Minnesota' },
  { value: 'mississippi', label: 'Mississippi' },
  { value: 'missouri', label: 'Missouri' },
  { value: 'montana', label: 'Montana' },
  { value: 'nebraska', label: 'Nebraska' },
  { value: 'nevada', label: 'Nevada' },
  { value: 'new_hampshire', label: 'New Hampshire' },
  { value: 'new_jersey', label: 'New Jersey' },
  { value: 'new_mexico', label: 'New Mexico' },
  { value: 'new_york', label: 'New York' },
  { value: 'north_carolina', label: 'North Carolina' },
  { value: 'north_dakota', label: 'North Dakota' },
  { value: 'ohio', label: 'Ohio' },
  { value: 'oklahoma', label: 'Oklahoma' },
  { value: 'oregon', label: 'Oregon' },
  { value: 'pennsylvania', label: 'Pennsylvania' },
  { value: 'rhode_island', label: 'Rhode Island' },
  { value: 'south_carolina', label: 'South Carolina' },
  { value: 'south_dakota', label: 'South Dakota' },
  { value: 'tennessee', label: 'Tennessee' },
  { value: 'texas', label: 'Texas' },
  { value: 'utah', label: 'Utah' },
  { value: 'vermont', label: 'Vermont' },
  { value: 'virginia', label: 'Virginia' },
  { value: 'washington', label: 'Washington' },
  { value: 'west_virginia', label: 'West Virginia' },
  { value: 'wisconsin', label: 'Wisconsin' },
  { value: 'wyoming', label: 'Wyoming' },
  { value: 'district_of_columbia', label: 'District of Columbia' }
];

// Canadian provinces list for dropdown
const canadianProvinces = [
  { value: 'alberta', label: 'Alberta' },
  { value: 'british_columbia', label: 'British Columbia' },
  { value: 'manitoba', label: 'Manitoba' },
  { value: 'new_brunswick', label: 'New Brunswick' },
  { value: 'newfoundland', label: 'Newfoundland and Labrador' },
  { value: 'northwest_territories', label: 'Northwest Territories' },
  { value: 'nova_scotia', label: 'Nova Scotia' },
  { value: 'nunavut', label: 'Nunavut' },
  { value: 'ontario', label: 'Ontario' },
  { value: 'pei', label: 'Prince Edward Island' },
  { value: 'quebec', label: 'Quebec' },
  { value: 'saskatchewan', label: 'Saskatchewan' },
  { value: 'yukon', label: 'Yukon' }
];

/**
 * LocationStep Component
 * First step in the checkout process for selecting country and province/state
 */
const LocationStep = () => {
  // Access checkout context
  const { customerInfo, updateCustomerInfo, setCurrentStep } = useCheckout();
  
  // Form state
  const [country, setCountry] = useState(customerInfo.country || '');
  const [province, setProvince] = useState(customerInfo.province || '');
  const [state, setState] = useState(customerInfo.state || '');
  const [errors, setErrors] = useState({});

  // Effect to update region selection when country changes
  useEffect(() => {
    if (country !== 'canada') {
      setProvince('');
    }
    if (country !== 'usa') {
      setState('');
    }
  }, [country]);

  // Validate form before proceeding
  const validateForm = () => {
    const newErrors = {};
    
    if (!country) {
      newErrors.country = 'Please select your country';
    }
    
    if (country === 'canada' && !province) {
      newErrors.province = 'Please select your province';
    }
    
    if (country === 'usa' && !state) {
      newErrors.state = 'Please select your state';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleContinue = () => {
    if (validateForm()) {
      // Update context with location data
      updateCustomerInfo({
        country,
        province,
        state
      });
      
      // Move to next step
      setCurrentStep(1);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Where are you located?
        </Typography>
        
        <Box component="form" sx={{ mt: 3 }}>
          <FormControl 
            fullWidth 
            margin="normal" 
            error={!!errors.country}
          >
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              id="country"
              value={country}
              label="Country"
              onChange={(e) => setCountry(e.target.value)}
            >
              <MenuItem value="" disabled>Select your country</MenuItem>
              <MenuItem value="canada">Canada</MenuItem>
              <MenuItem value="usa">United States</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {errors.country && (
              <FormHelperText>{errors.country}</FormHelperText>
            )}
          </FormControl>
          
          {country === 'canada' && (
            <FormControl 
              fullWidth 
              margin="normal" 
              error={!!errors.province}
            >
              <InputLabel id="province-label">Province</InputLabel>
              <Select
                labelId="province-label"
                id="province"
                value={province}
                label="Province"
                onChange={(e) => setProvince(e.target.value)}
              >
                <MenuItem value="" disabled>Select your province</MenuItem>
                {canadianProvinces.map((province) => (
                  <MenuItem key={province.value} value={province.value}>
                    {province.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.province && (
                <FormHelperText>{errors.province}</FormHelperText>
              )}
            </FormControl>
          )}
          
          {country === 'usa' && (
            <FormControl 
              fullWidth 
              margin="normal" 
              error={!!errors.state}
            >
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                value={state}
                label="State"
                onChange={(e) => setState(e.target.value)}
              >
                <MenuItem value="" disabled>Select your state</MenuItem>
                {usStates.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.state && (
                <FormHelperText>{errors.state}</FormHelperText>
              )}
            </FormControl>
          )}
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={handleContinue}
            >
              Continue to Payment
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LocationStep;